import { ModalBody, ModalHeader } from '@stenajs-webui/modal';
import { Suspense } from 'react';
import { useHasPermission } from '../../../common/hooks/useHasPermission';
import { EditableProvider } from '../../../common/modal/EditableContext';
import { Loading } from '../../../common-ui/Loading';
import { Permission } from '../../../services/authentication/utils/authUtils';
import { useIntermodalVehicleTypesBackgroundQuery } from '../../../services/intermodal/hooks/useAllIntermodalVehicleTypesAsOptions';
import { useGoodsTypesBackgroundQuery } from '../../../services/intermodal/hooks/useGoodsTypes';
import { useIntermodalBookingsDetails } from '../../../services/intermodal/hooks/useIntermodalBookingsDetails';
import { initializeUpdateIntermodalFormState } from '../../../services/intermodal/updateIntermodalReducer';
import { useAppDispatch } from '../../../store/appDispatch';
import {
  BookingErrorModalContents,
  getErrorModalContentFromGQLError,
} from '../../manage/details/components/BookingErrorModalContents';
import { transformIntermodalBookingToUpdateFormState } from '../utils/detailsUtils';
import { IntermodalBookingUpdate } from './IntermodalBookingUpdate';
import { IntermodalBookingUpdateFooter } from './IntermodalBookingUpdateFooter';
import { IntermodalBookingUpdateHeader } from './IntermodalBookingUpdateHeader';

interface IntermodalBookingUpdateModalContainerProps {
  bookingNo: number;
  onRequestClose: () => void;
}

export function IntermodalBookingUpdateModalContainer({
  bookingNo,
  onRequestClose,
}: IntermodalBookingUpdateModalContainerProps) {
  const dispatch = useAppDispatch();
  const goodsTypesQueryRef = useGoodsTypesBackgroundQuery();
  const vehicleTypesQueryRef = useIntermodalVehicleTypesBackgroundQuery();

  const getHasPermission = useHasPermission();
  const hasPermission = getHasPermission(Permission.EditIntermodalBookings);

  const { booking, loading, error, refetch } = useIntermodalBookingsDetails(bookingNo, data => {
    if (data.viewer.intermodalBookings.booking) {
      dispatch(
        initializeUpdateIntermodalFormState(
          transformIntermodalBookingToUpdateFormState(data.viewer.intermodalBookings.booking),
        ),
      );
    }
  });

  if (loading) {
    return (
      <ModalBody>
        <Loading />
      </ModalBody>
    );
  }

  if (error || !booking) {
    const { message, icon } = getErrorModalContentFromGQLError(error);
    return (
      <ModalBody>
        <ModalHeader
          heading={message}
          onClickClose={onRequestClose}
          closeButtonAriaLabel={'Close'}
        />
        <BookingErrorModalContents icon={icon} />
      </ModalBody>
    );
  }

  return (
    <EditableProvider value={booking?.editable === true && hasPermission}>
      <Suspense
        fallback={
          <ModalBody>
            <Loading />
          </ModalBody>
        }
      >
        <ModalBody maxWidth={1600}>
          <IntermodalBookingUpdateHeader
            className={'t_update_intermodal_header'}
            booking={booking}
            onClose={onRequestClose}
          />
          <IntermodalBookingUpdate
            booking={booking}
            goodsTypesQueryRef={goodsTypesQueryRef}
            vehicleTypesQueryRef={vehicleTypesQueryRef}
          />
        </ModalBody>
        <IntermodalBookingUpdateFooter booking={booking} refetch={refetch} />
      </Suspense>
    </EditableProvider>
  );
}
